<template>
  <ModalTemplate size="lg" :modal="modal" :closable="true">
    <div class="pb-3 md:pb-5 md:pt-2">
      <p class="mb-1 text-lg font-medium md:text-xl">
        {{ $t('titles.Filter') }}
      </p>
    </div>
    <div v-if="filter.types.length > 2" class="mb-3">
      <label
        class="mb-2 flex items-center justify-between gap-1 text-sm font-medium leading-none text-gray-500 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-neutral-400"
      >
        <span class="">
          {{ $t('filter.Type') }}
        </span>
      </label>

      <Select
        v-model="selectedType"
        button-class="form-control h-9 cursor-pointer"
        :options="filter.types"
        :chevron-size="16"
        :fullscreen="false"
      >
        <template #title>
          {{ $t('filter.Type') }}
        </template>
        <template #current-option="{ option }">
          <div class="">
            {{ $t('types.medium.' + option.name) }}
          </div>
        </template>
        <template #option="{ option }">
          {{ $t('types.medium.' + option.name) }}
        </template>
      </Select>
    </div>
    <div
      v-if="selectedType.name === 'living' || selectedType.name === 'all'"
      class="mb-3"
    >
      <label
        class="mb-2 flex items-center justify-between gap-1 text-sm font-medium leading-none text-gray-500 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-neutral-400"
      >
        <span class="">
          {{ $t('filter.Rooms count') }}
        </span>
      </label>
      <div
        class="custom-scroll custom-scroll-x flex snap-x scroll-ps-2 gap-1 overflow-auto pb-1"
      >
        <button
          v-for="room in filter.rooms"
          :key="room"
          class="min-w-10 h-10 cursor-pointer snap-start whitespace-nowrap rounded-lg bg-gray-100 px-3 text-sm hover:bg-gray-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
          :class="{
            'inset-ring-2 inset-ring-primary': selectedRooms.includes(room),
          }"
          @click="
            () => {
              if (selectedRooms.includes(room)) {
                selectedRooms = selectedRooms.filter((r) => r !== room);
              } else {
                selectedRooms.push(room);
              }
            }
          "
        >
          {{ checkLabelTranslation(room) ? $t(`titles.rooms.${room}`) : room }}
        </button>
      </div>
    </div>

    <div
      v-if="
        catalogSettings.show_price &&
        filter.price.min !== Infinity &&
        filter.price.max !== Infinity
      "
      class="mb-3"
    >
      <label
        class="mb-2 flex items-center justify-between gap-1 text-sm font-medium leading-none text-gray-500 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-neutral-400"
      >
        <span class="">
          {{ $t('filter.Price') }}
        </span>
        <span class="">
          {{ $t('labels.compactNumbers.' + numberFormat.symbol) }}
          {{ currencySymbol }}
        </span>
      </label>

      <RangeFilter
        id="priceFilter"
        v-model="price.value"
        :max="price.max"
        :min="price.min"
      ></RangeFilter>
    </div>
    <div class="mb-3">
      <label
        class="mb-2 flex items-center justify-between gap-1 text-sm font-medium leading-none text-gray-500 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-neutral-400"
      >
        <span class="">
          {{ $t('filter.Area') }}
        </span>
      </label>

      <RangeFilter
        id="priceFilter"
        v-model="areaRangeValue"
        :max="filter.area.max"
        :min="filter.area.min"
      ></RangeFilter>
    </div>

    <div v-if="tags.length" class="mb-3">
      <div
        class="custom-scroll custom-scroll-x flex snap-x scroll-ps-2 gap-1 overflow-auto py-1"
      >
        <button
          v-for="tag in tags"
          :key="tag.id"
          class="h-9 cursor-pointer snap-start whitespace-nowrap rounded-2xl bg-gray-100 px-3 text-sm hover:bg-gray-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
          :class="{
            'inset-ring-2 inset-ring-primary': selectedTags.includes(tag.id),
          }"
          @click="
            () => {
              if (selectedTags.includes(tag.id)) {
                selectedTags = selectedTags.filter((r) => r !== tag.id);
              } else {
                selectedTags.push(tag.id);
              }
            }
          "
        >
          {{ tag.name }}
        </button>
      </div>
    </div>

    <button
      class="btn btn-primary mb-4 mt-6 h-10 w-full justify-center"
      @click="submitFilter"
    >
      {{ $t('control.Search') }}
    </button>
    <div class="mb-2 flex justify-center">
      <button
        class="btn hover:text-primary gap-1 text-gray-400 dark:text-neutral-400"
        @click="clearFilter"
      >
        {{ $t('control.Clear filter') }}
        <lucide-icon icon="X" :size="16" class="mb-0.5"></lucide-icon>
      </button>
    </div>
  </ModalTemplate>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ModalTemplate from '@/components/modal/ModalTemplate.vue';
import RangeFilter from '@/components/ui/RangeFilter.vue';
import Select from '@/components/ui/Select.vue';

import {
  getCurrencySymbol,
  getNumberFormat,
  checkLabelTranslation,
} from '@dmlnc/hello_world_shared';

export default {
  name: 'FilterModal',
  components: {
    ModalTemplate,
    RangeFilter,
    Select,
  },
  props: {
    modal: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      numberFormat: null,
      areaRangeValue: [0, 1],
      price: {
        min: 0,
        max: 1,
        value: [0, 1],
      },
      selectedType: {
        id: 0,
        name: 'all',
      },
      selectedTags: [],
      selectedRooms: [],
    };
  },
  computed: {
    ...mapGetters([
      'catalogSettings',
      'filter',
      'tags',
      'currentLanguage',
      'currentCurrency',
      'currentFilter',
    ]),

    currencySymbol() {
      return getCurrencySymbol(this.currentCurrency, this.$i18n.locale);
    },
  },
  created() {
    this.numberFormat = getNumberFormat(this.filter.price.min);
    const minFormated = Math.floor(
      this.filter.price.min / this.numberFormat.value
    );
    const maxFormated = Math.ceil(
      this.filter.price.max / this.numberFormat.value
    );

    this.areaRangeValue = [this.filter.area.min, this.filter.area.max];

    this.price = {
      min: minFormated,
      max: maxFormated,
      value: [minFormated, maxFormated],
    };

    if (this.currentFilter) {
      const filter = this.currentFilter;

      if (filter.type) {
        this.selectedType = this.filter.types.find(
          (t) => t.name === filter.type
        ) || { id: 0, name: 'all' };
      }

      if (filter.rooms && Array.isArray(filter.rooms)) {
        this.selectedRooms = filter.rooms;
      }

      if (filter.tags && Array.isArray(filter.tags)) {
        this.selectedTags = filter.tags;
      }

      if (filter.min_area) {
        this.areaRangeValue[0] = Math.max(
          Number(filter.min_area),
          this.filter.area.min
        );
      }

      if (filter.max_area) {
        this.areaRangeValue[1] = Math.min(
          Number(filter.max_area),
          this.filter.area.max
        );
      }

      if (filter.min_price) {
        this.price.value[0] = Math.max(
          Math.floor(filter.min_price / this.numberFormat.value),
          this.price.min
        );
      }

      if (filter.max_price) {
        this.price.value[1] = Math.min(
          Math.ceil(filter.max_price / this.numberFormat.value),
          this.price.max
        );
      }
    }
  },
  mounted() {},
  methods: {
    ...mapActions(['CLOSE_MODAL']),
    checkLabelTranslation(room) {
      return checkLabelTranslation(room);
    },
    submitFilter() {
      const query = {
        type: this.selectedType.id != 0 ? this.selectedType.name : undefined,
        tags: this.selectedTags.length
          ? JSON.stringify(this.selectedTags)
          : undefined,
        rooms:
          this.selectedRooms.length === this.filter.rooms.length
            ? undefined
            : this.selectedRooms.length
            ? JSON.stringify(this.selectedRooms)
            : undefined,
        min_area:
          this.areaRangeValue[0] > this.filter.area.min
            ? this.areaRangeValue[0]
            : undefined,
        max_area:
          this.areaRangeValue[1] < this.filter.area.max
            ? this.areaRangeValue[1]
            : undefined,
        min_price:
          this.price.value[0] > this.price.min
            ? this.price.value[0] * this.numberFormat.value
            : undefined,
        max_price:
          this.price.value[1] < this.price.max
            ? this.price.value[1] * this.numberFormat.value
            : undefined,
      };

      // Remove undefined values from query
      Object.keys(query).forEach(
        (key) => query[key] === undefined && delete query[key]
      );

      this.routerPush({
        name: 'Premises',
        query,
      });

      this.CLOSE_MODAL();
    },

    clearFilter() {
      this.selectedType = { id: 0, name: 'all' };
      this.selectedTags = [];
      this.selectedRooms = [];
      this.areaRangeValue = [this.filter.area.min, this.filter.area.max];
      this.price.value = [this.price.min, this.price.max];
      this.submitFilter();
    },
  },
};
</script>
